import { useSyncExternalStore } from "react";

/**
 * What `Skip all` / `Include all` mean while a search is narrowing the list — a
 * prototype-only dial, not a product setting.
 *
 * With nothing typed the two are identical: the action takes the whole tab. The
 * question is only what "all" means once the list on screen is not the whole
 * tab any more:
 *
 *  - `disable` — the action is refused while the list is filtered, and says so.
 *    Nothing can happen that the user cannot see.
 *  - `act_on_matches` — the action takes what matched, and the button counts it:
 *    `Skip these 5`. Nothing hidden moves either, and the search becomes a way
 *    of picking a subset rather than something to clear first.
 */
export type BulkActionBehavior = "disable" | "act_on_matches";

export const BULK_ACTION_BEHAVIORS: { id: BulkActionBehavior; label: string }[] = [
  { id: "disable", label: "Disable" },
  { id: "act_on_matches", label: "Act on matches" },
];

const KEY = "brs-bulk-action-behavior";
const DEFAULT: BulkActionBehavior = "disable";

function read(): BulkActionBehavior {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored === "disable" || stored === "act_on_matches") return stored;
  } catch {
    /* fall through to the default */
  }
  return DEFAULT;
}

let current: BulkActionBehavior = read();
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setBulkActionBehavior(next: BulkActionBehavior): void {
  if (next === current) return;
  current = next;
  try {
    localStorage.setItem(KEY, next);
  } catch {
    /* the choice just doesn't survive a reload */
  }
  for (const listener of listeners) listener();
}

export function useBulkActionBehavior(): BulkActionBehavior {
  return useSyncExternalStore(
    subscribe,
    () => current,
    () => DEFAULT,
  );
}
